import { Component } from '@theme/component';

const PREFERENCE_KEY = 'nuzzle-motion-preference-v1';
const FLOAT_SELECTOR = '.nr-category-circles img, .nh-gift-grid > a > img';
const GALLERY_SELECTOR = '[data-detail-main]';
const REVEAL_SELECTOR = '.nr-categories, .nr-feature, .nr-shelf, .nr-catalog__intro, .nh-hero, .nh-action-card, .np-help-callout, .nr-recent, .nr-saved__intro';
const revealedNodes = new WeakSet();
let memoryPaused;

function readPreference() {
  if (memoryPaused !== undefined) return memoryPaused;
  try { memoryPaused = localStorage.getItem(PREFERENCE_KEY) === 'paused'; }
  catch { memoryPaused = false; }
  return memoryPaused;
}

/** A single document-level motion coordinator; content stays visible without enhancement. */
class RetailMotion extends Component {
  #controller;
  #observer;
  #mutations;
  #media;
  #targets = new Map();
  #animations = new Map();
  #pendingPhotos = new WeakSet();
  #paused = true;
  #userPaused = false;
  #scanQueued = false;
  #lastSaveTarget;
  #saveTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.#userPaused = Boolean(document.querySelector('[data-nr-motion-toggle]')) && readPreference();
    this.#media = matchMedia('(prefers-reduced-motion: reduce)');
    this.#media.addEventListener('change', () => this.#applyPolicy(), options);
    document.addEventListener('visibilitychange', () => this.#applyPolicy(), options);
    window.addEventListener('storage', (event) => {
      if (!document.querySelector('[data-nr-motion-toggle]') || (event.key !== PREFERENCE_KEY && event.key !== null)) return;
      memoryPaused = event.newValue === 'paused';
      this.#userPaused = memoryPaused;
      this.#applyPolicy();
    }, options);
    document.addEventListener('click', (event) => {
      const toggle = event.target instanceof Element ? event.target.closest('[data-nr-motion-toggle]') : null;
      if (toggle) {
        if (this.#media.matches) return;
        this.#userPaused = !this.#userPaused;
        memoryPaused = this.#userPaused;
        try { localStorage.setItem(PREFERENCE_KEY, this.#userPaused ? 'paused' : 'playing'); } catch { /* In-memory preference remains effective. */ }
        this.#applyPolicy(true);
        return;
      }
      this.#lastSaveTarget = event.target instanceof Element ? event.target.closest('[data-save-gift]') : null;
      clearTimeout(this.#saveTimer);
      this.#saveTimer = setTimeout(() => { this.#lastSaveTarget = null; }, 0);
    }, { ...options, capture: true });
    document.addEventListener('load', (event) => {
      const image = event.target;
      if (image instanceof HTMLImageElement && this.#pendingPhotos.has(image)) {
        this.#pendingPhotos.delete(image);
        this.#photoFeedback(image);
      }
    }, { ...options, capture: true });
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const state = this.#targets.get(entry.target);
          if (!state) continue;
          state.visible = entry.isIntersecting;
          if (state.float) entry.target.dataset.nrMotionVisible = String(state.visible);
          if (state.visible && state.reveal) this.#reveal(entry.target);
          if (!state.visible) { this.#animations.get(entry.target)?.cancel(); this.#animations.delete(entry.target); delete entry.target.dataset.nrMotionAnimating; }
        }
      }, { threshold: 0.12 });
    }
    this.#scan();
    this.#mutations = new MutationObserver((records) => this.#onMutations(records));
    this.#mutations.observe(document.body, { subtree: true, childList: true, attributes: true, attributeOldValue: true, attributeFilter: ['src', 'aria-pressed'] });
    document.documentElement.classList.add('nr-motion-ready');
    this.hidden = false;
    this.#applyPolicy();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#mutations?.disconnect();
    clearTimeout(this.#saveTimer);
    for (const [node, animation] of this.#animations) { animation.cancel(); delete node.dataset.nrMotionAnimating; }
    this.#animations.clear();
    for (const node of this.#targets.keys()) this.#cleanTarget(node);
    this.#targets.clear();
    this.#pendingPhotos = new WeakSet();
    this.#lastSaveTarget = null;
    document.documentElement.classList.remove('nr-motion-ready', 'nr-motion-paused');
    super.disconnectedCallback();
  }

  #applyPolicy(announce = false) {
    const reduced = this.#media.matches;
    this.#paused = this.#userPaused || reduced || document.hidden;
    document.documentElement.classList.toggle('nr-motion-paused', this.#paused);
    this.#updateControls();
    if (announce) {
      const status = this.querySelector('[data-nr-motion-status]');
      if (status) status.textContent = this.#userPaused ? this.dataset.pausedMessage : this.dataset.playingMessage;
    }
    if (this.#paused) {
      for (const [node, animation] of this.#animations) { animation.cancel(); delete node.dataset.nrMotionAnimating; }
      this.#animations.clear();
    } else {
      for (const [node, state] of this.#targets) if (state.visible && state.reveal) this.#reveal(node);
    }
    document.dispatchEvent(new CustomEvent('nr:motion-change', { detail: { paused: this.#paused, userPaused: this.#userPaused, reduced, hidden: document.hidden } }));
  }

  #updateControls() {
    const reduced = this.#media.matches;
    for (const button of document.querySelectorAll('[data-nr-motion-toggle]')) {
      button.hidden = false;
      button.disabled = reduced;
      button.setAttribute('aria-pressed', String(this.#userPaused || reduced));
      button.dataset.paused = String(this.#userPaused || reduced);
      const label = button.querySelector('[data-nr-motion-label]') || button;
      const text = reduced ? this.dataset.reducedLabel : this.#userPaused ? (button.dataset.labelPlay || this.dataset.playLabel) : (button.dataset.labelPause || this.dataset.pauseLabel);
      if (label.textContent !== text) label.textContent = text;
    }
  }

  #scan() {
    for (const node of document.querySelectorAll(`${FLOAT_SELECTOR}, ${REVEAL_SELECTOR}, ${GALLERY_SELECTOR}`)) {
      if (node.closest('.nr-hero') || this.#targets.has(node)) continue;
      const float = node.matches(FLOAT_SELECTOR);
      const state = { float, reveal: node.matches(REVEAL_SELECTOR), visible: false };
      this.#targets.set(node, state);
      if (float) {
        node.dataset.nrMotionFloat = '';
        node.style.setProperty('--nr-motion-base-scale', node.closest('.nr-category-circles') ? '1.06' : '1');
        const group = node.closest('.nr-category-circles, .nh-gift-grid');
        const siblings = group ? [...group.querySelectorAll('img')] : [node];
        const index = siblings.indexOf(node);
        node.style.setProperty('--nr-motion-delay', `${Math.max(index, 0) * 2.4}s`);
        node.style.setProperty('--nr-motion-duration', `${Math.max(4.8, siblings.length * 2.4)}s`);
      }
      this.#observer?.observe(node);
    }
    for (const [node] of this.#targets) {
      if (node.isConnected) continue;
      this.#observer?.unobserve(node);
      this.#animations.get(node)?.cancel();
      this.#animations.delete(node);
      this.#cleanTarget(node);
      this.#targets.delete(node);
    }
    this.#updateControls();
  }

  #cleanTarget(node) {
    delete node.dataset.nrMotionFloat;
    delete node.dataset.nrMotionVisible;
    delete node.dataset.nrMotionAnimating;
    for (const property of ['--nr-motion-base-scale', '--nr-motion-delay', '--nr-motion-duration']) node.style.removeProperty(property);
  }

  #reveal(node) {
    if (this.#paused || revealedNodes.has(node)) return;
    revealedNodes.add(node);
    this.#animate(node, [{ opacity: .88, transform: 'translateY(12px)' }, { opacity: 1, transform: 'translateY(0)' }], 560);
  }

  #animate(node, keyframes, duration) {
    if (this.#paused || !node.isConnected || typeof node.animate !== 'function') return;
    this.#animations.get(node)?.cancel();
    node.dataset.nrMotionAnimating = '';
    const animation = node.animate(keyframes, { duration, easing: 'cubic-bezier(.22,.68,.3,1)', fill: 'none' });
    this.#animations.set(node, animation);
    animation.finished.then(() => this.#finishAnimation(node, animation), () => this.#finishAnimation(node, animation));
  }

  #finishAnimation(node, animation) {
    if (this.#animations.get(node) !== animation) return;
    this.#animations.delete(node);
    delete node.dataset.nrMotionAnimating;
  }

  #photoFeedback(image) {
    if (this.#targets.get(image)?.visible && !image.closest('.nr-hero')) this.#animate(image, [{ opacity: .65 }, { opacity: 1 }], 200);
  }

  #onMutations(records) {
    let rescan = false;
    for (const record of records) {
      if (record.type === 'childList') { if (record.addedNodes.length || record.removedNodes.length) rescan = true; continue; }
      const node = record.target;
      if (this.#paused || !(node instanceof Element) || node.closest('.nr-hero')) continue;
      if (record.attributeName === 'src' && node.matches('[data-detail-main]') && record.oldValue !== node.getAttribute('src')) {
        if (node.complete && node.naturalWidth) this.#photoFeedback(node); else this.#pendingPhotos.add(node);
      }
      if (record.attributeName === 'aria-pressed' && node === this.#lastSaveTarget && node.matches('[data-save-gift]') && record.oldValue !== 'true' && node.getAttribute('aria-pressed') === 'true') {
        const heart = node.querySelector('svg') || node;
        this.#animate(heart, [{ transform: 'scale(.88)' }, { transform: 'scale(1.16)', offset: .55 }, { transform: 'scale(1)' }], 300);
      }
    }
    if (rescan && !this.#scanQueued) {
      this.#scanQueued = true;
      queueMicrotask(() => { this.#scanQueued = false; if (this.isConnected) this.#scan(); });
    }
  }
}
if (!customElements.get('nr-motion')) customElements.define('nr-motion', RetailMotion);
